import { setAppLayoutTestId } from "@bcc/components/app-layout.utils";
import { useEffect, useState } from "react";
import { testIdSelector } from "./tour.utils";
import { isAppLoading } from "./use-tour-target";

/** The app layout's own loading placeholder (services + installations fetch) */
const APP_LAYOUT_LOADING_SELECTOR = testIdSelector(setAppLayoutTestId("loading"));
const POLL_INTERVAL_MS = 200;

/** Busy while the app layout is still loading or any shared LoadingIcon (other than the tour's own) is on screen */
export const isPageBusy = (): boolean => document.querySelector(APP_LAYOUT_LOADING_SELECTOR) !== null || isAppLoading();

export interface UsePageReadyOptions {
  /** Readiness is latched; a new key re-arms it (e.g. when the page changes) */
  resetKey?: string;
  /** Also wait for this selector to be on screen */
  readySelector?: string;
}

/**
 * True once the page has finished loading. While `active` and not yet ready it
 * checks the DOM immediately and then every POLL_INTERVAL_MS, so callers can
 * hold UI such as the tour welcome dialog until real content is on screen.
 */
export function usePageReady(active: boolean, { resetKey, readySelector }: UsePageReadyOptions = {}): boolean {
  const [ready, setReady] = useState<{ key?: string; value: boolean }>({ key: resetKey, value: false });
  const isReady = ready.key === resetKey && ready.value;

  useEffect(() => {
    if (!active || isReady) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      if (!isPageBusy() && (readySelector === undefined || document.querySelector(readySelector) !== null)) {
        setReady({ key: resetKey, value: true });
        return;
      }
      timer = setTimeout(check, POLL_INTERVAL_MS);
    };
    check();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [active, isReady, resetKey, readySelector]);

  return isReady;
}
